import {
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
} from '@mui/material';
import { SalesVsExpensesPageQuery } from '../../../../../../services';
import { useMemo } from 'react';
import { formatFloat, formatLongString } from '../../../../../../helpers';
import { useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';

export function SalesTable({
    salesVsExpensesQuery,
    showDetails,
    loading = false,
}: {
    salesVsExpensesQuery?: SalesVsExpensesPageQuery;
    showDetails: boolean;
    loading?: boolean;
}) {
    const theme = useTheme();
    const alternateColor = theme.palette.mode === 'dark' ? '#303030' : '#E0E0E0';
    const processedData = useMemo(() => {
        const salesProductsSummaryByResourceCategory: SalesVsExpensesPageQuery['getSalesSummaryByCategory']['sales'] =
            salesVsExpensesQuery?.getSalesSummaryByCategory.sales || [];

        const salesProductsSummaryByAll: SalesVsExpensesPageQuery['getSalesProductsSummaryByAll']['sales'] =
            salesVsExpensesQuery?.getSalesProductsSummaryByAll.sales || [];

        return salesProductsSummaryByResourceCategory
            .map((saleByProductCategory) => {
                return {
                    ...saleByProductCategory,
                    children: salesProductsSummaryByAll
                        .filter((saleByAll) => {
                            return (
                                saleByAll.product_category_id ===
                                saleByProductCategory.product_category_id
                            );
                        })
                        .sort((a, b) => b.total_with_tax - a.total_with_tax),
                };
            })
            .sort((a, b) => {
                const aVal = a.product_category_name || '';
                const bVal = b.product_category_name || '';
                return aVal.localeCompare(bVal);
            });
    }, [salesVsExpensesQuery]);

    return (
        <>
            <Typography sx={{ mb: 2}}>
                Ventas
            </Typography>
            <TableContainer>
                <Table size={'small'} padding={'none'}>
                    <TableRow>
                        <TableCell colSpan={2} />
                        <TableCell>Kilos</TableCell>
                        <TableCell>Precio Kilo</TableCell>
                        <TableCell>Bultos</TableCell>
                        <TableCell>Precio Bulto</TableCell>
                        <TableCell>Total</TableCell>
                    </TableRow>
                    <TableBody>
                        {loading && processedData.length === 0
                            ? Array.from({ length: 5 }).map((_, r) => (
                                  <TableRow
                                      key={`skeleton-${r}`}
                                      sx={{
                                          backgroundColor:
                                              r % 2 === 1
                                                  ? alternateColor
                                                  : undefined,
                                      }}
                                  >
                                      {Array.from({ length: 7 }).map(
                                          (__, c) => (
                                              <TableCell key={c} sx={{ px: 1 }}>
                                                  <Skeleton variant="text" />
                                              </TableCell>
                                          ),
                                      )}
                                  </TableRow>
                              ))
                            : processedData.map((saleByProduct, i) => {
                            return (
                                <>
                                    <TableRow
                                        sx={{
                                            backgroundColor:
                                                i % 2 === 1
                                                    ? alternateColor
                                                    : undefined,
                                            px: 1,
                                        }}
                                        key={saleByProduct.product_category_id}
                                    >
                                        <TableCell
                                            sx={{
                                                textAlign: 'left',
                                                fontWeight: showDetails
                                                    ? 'bold'
                                                    : undefined,
                                                px: 1,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {
                                                saleByProduct.product_category_name
                                            }
                                        </TableCell>
                                        <TableCell />
                                        <TableCell
                                            sx={{
                                                textAlign: 'right',
                                                fontWeight: showDetails
                                                    ? 'bold'
                                                    : undefined,
                                                px: 1,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {saleByProduct.kilos_sold !== 0 ? formatFloat(
                                                saleByProduct.kilos_sold,
                                            ) + ' kg' : ''}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                textAlign: 'right',
                                                fontWeight: showDetails
                                                    ? 'bold'
                                                    : undefined,
                                                px: 1,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {saleByProduct.kilos_sold !== 0 ? formatFloat(
                                                saleByProduct.total_with_tax / saleByProduct.kilos_sold,
                                            ) : ''}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                textAlign: 'right',
                                                fontWeight: showDetails
                                                    ? 'bold'
                                                    : undefined,
                                                px: 1,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            { saleByProduct.groups_sold !== 0 ?formatFloat(
                                                saleByProduct.groups_sold,
                                            ) : ''}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                textAlign: 'right',
                                                fontWeight: showDetails
                                                    ? 'bold'
                                                    : undefined,
                                                px: 1,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            { saleByProduct.groups_sold !== 0 ?formatFloat(
                                                saleByProduct.total_with_tax / saleByProduct.groups_sold,
                                            ) : ''}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                textAlign: 'right',
                                                fontWeight: showDetails
                                                    ? 'bold'
                                                    : undefined,
                                                px: 1,
                                            }}
                                        >
                                            {formatFloat(
                                                saleByProduct.total_with_tax,
                                            )}
                                        </TableCell>
                                    </TableRow>
                                    {showDetails
                                        ? saleByProduct.children.map(
                                              (saleByAll, j) => {
                                                  return (
                                                      <TableRow
                                                          sx={{
                                                              backgroundColor:
                                                                  j % 2 === 1
                                                                      ? alternateColor
                                                                      : undefined,
                                                          }}
                                                          key={
                                                              saleByAll.account_id
                                                          }
                                                      >
                                                          <TableCell />
                                                          <TableCell
                                                              sx={{
                                                                  textAlign:
                                                                      'left',
                                                                  px: 1,
                                                                  whiteSpace:
                                                                      'nowrap',
                                                              }}
                                                          >
                                                              {formatLongString(
                                                                  saleByAll.account_name,
                                                              )}
                                                          </TableCell>
                                                          <TableCell
                                                              sx={{
                                                                  textAlign:
                                                                      'right',
                                                                  px: 1,
                                                                  whiteSpace:
                                                                      'nowrap',
                                                              }}
                                                          >
                                                              {saleByAll.kilos_sold !== 0 ? formatFloat(
                                                                  saleByAll.kilos_sold,
                                                              ) + ' kg' : ''}
                                                          </TableCell>
                                                          <TableCell
                                                              sx={{
                                                                  textAlign:
                                                                      'right',
                                                                  px: 1,
                                                                  whiteSpace:
                                                                      'nowrap',
                                                              }}
                                                          >
                                                              {saleByAll.kilos_sold !== 0 ? formatFloat(
                                                                  saleByAll.total_with_tax / saleByAll.kilos_sold,
                                                              )  : ''}
                                                          </TableCell>
                                                          <TableCell
                                                              sx={{
                                                                  textAlign:
                                                                      'right',
                                                                  px: 1,
                                                                  whiteSpace:
                                                                      'nowrap',
                                                              }}
                                                          >
                                                              {saleByAll.groups_sold !== 0 ? formatFloat(
                                                                  saleByAll.groups_sold,
                                                              ) : ''}
                                                          </TableCell>
                                                          <TableCell
                                                              sx={{
                                                                  textAlign:
                                                                      'right',
                                                                  px: 1,
                                                                  whiteSpace:
                                                                      'nowrap',
                                                              }}
                                                          >
                                                              {saleByAll.groups_sold !== 0 ? formatFloat(
                                                                  saleByAll.total_with_tax / saleByAll.groups_sold,
                                                              ) : ''}
                                                          </TableCell>
                                                          <TableCell
                                                              sx={{
                                                                  textAlign:
                                                                      'right',
                                                                  px: 1,
                                                              }}
                                                          >
                                                              {formatFloat(
                                                                  saleByAll.total_with_tax,
                                                              )}
                                                          </TableCell>
                                                      </TableRow>
                                                  );
                                              },
                                          )
                                        : null}
                                </>
                            );
                              })}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
}
